import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { PHOTO_LIMITS, tierFromChef, type PremiumTier } from "./chef-utils";

/**
 * Public chef DTO returned by the read-only server functions.
 * Computed flags (is_premium, is_sponsored) are derived from the timestamp
 * columns so the client doesn't have to recompute them every render.
 */
export type PublicChef = {
  user_id: string;
  slug: string;
  display_name: string;
  headline: string | null;
  bio: string | null;
  city: string | null;
  region: string | null;
  cover_url: string | null;
  cuisines: string[];
  specialties: string[];
  languages: string[];
  hourly_rate: number | null;
  min_price: number | null;
  max_price: number | null;
  years_experience: number | null;
  rating_avg: number;
  rating_count: number;
  verified_badge: boolean;
  founder_badge: boolean;
  is_premium: boolean;
  /** Tier derivato (free / silver / gold). */
  premium_tier: PremiumTier;
  is_sponsored: boolean;
  created_at: string;
};

export type PublicChefMedia = {
  id: string;
  kind: "photo" | "video" | "menu" | "cover" | "avatar";
  url: string;
  is_cover: boolean;
  position: number;
};

const PROJECTION =
  // whatsapp NON viene esposto pubblicamente: contatto diretto sempre via /chef/$slug
  // dietro form contact_requests, mai esposto a scraping anonimo.
  "user_id, slug, display_name, headline, bio, city, region, cover_url, cuisines, specialties, languages, hourly_rate, min_price, max_price, years_experience, rating_avg, rating_count, verified_badge, founder_badge, premium_until, premium_tier, sponsored_until, created_at";

type Row = Record<string, unknown> & {
  premium_until: string | null;
  premium_tier: string | null;
  sponsored_until: string | null;
  rating_avg: number | string | null;
  rating_count: number | string | null;
  hourly_rate: number | string | null;
  min_price: number | string | null;
  max_price: number | string | null;
};

function mapRow(r: Row): PublicChef {
  const now = Date.now();
  const premiumUntil = r.premium_until ? new Date(r.premium_until).getTime() : 0;
  const sponsoredUntil = r.sponsored_until ? new Date(r.sponsored_until).getTime() : 0;
  const tier = tierFromChef({ premium_tier: r.premium_tier, premium_until: r.premium_until });
  const num = (v: number | string | null | undefined) => (v == null ? null : Number(v));
  return {
    user_id: r.user_id as string,
    slug: r.slug as string,
    display_name: r.display_name as string,
    headline: (r.headline as string | null) ?? null,
    bio: (r.bio as string | null) ?? null,
    city: (r.city as string | null) ?? null,
    region: (r.region as string | null) ?? null,
    cover_url: (r.cover_url as string | null) ?? null,
    cuisines: (r.cuisines as string[] | null) ?? [],
    specialties: (r.specialties as string[] | null) ?? [],
    languages: (r.languages as string[] | null) ?? [],
    hourly_rate: num(r.hourly_rate),
    min_price: num(r.min_price),
    max_price: num(r.max_price),
    years_experience: (r.years_experience as number | null) ?? null,
    rating_avg: Number(r.rating_avg ?? 0),
    rating_count: Number(r.rating_count ?? 0),
    verified_badge: !!r.verified_badge,
    founder_badge: !!r.founder_badge,
    is_premium: premiumUntil > now,
    premium_tier: tier,
    is_sponsored: sponsoredUntil > now,
    created_at: r.created_at as string,
  };
}

const listInput = z.object({ limit: z.number().int().min(1).max(200).optional() });

export const listPublicChefs = createServerFn({ method: "GET" })
  .validator((d: { limit?: number } | undefined) => listInput.parse(d ?? {}))
  .handler(async ({ data }) => {
    const { getPublicSupabase } = await import("./supabase.public.server");
    const sb = getPublicSupabase();
    // Sprint 23.6 — uno chef compare pubblicamente SOLO se ha almeno un
    // annuncio attivo. Usiamo un inner join lato PostgREST su chef_listings
    // filtrato per is_active=true: i chef senza annunci vengono esclusi.
    const { data: rows, error } = await sb
      .from("chefs")
      .select(`${PROJECTION}, chef_listings!inner(id)`)
      .eq("chef_listings.is_active", true)
      .eq("is_visible", true)
      .eq("verified_badge", true)
      .order("rating_avg", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(data.limit ?? 60);
    if (error) throw new Error(error.message);
    const chefs = ((rows ?? []) as unknown as Row[]).map(mapRow);
    // Premium (non scaduto) e Sponsored hanno priorità in listing.
    // Premium scaduti NON ricevono boost: l'ordine secondario è rating.
    // Sprint 19.1 — Ranking deterministico:
    //   Sponsored > Gold > Silver > Verificati > Normali.
    chefs.sort((a, b) => {
      const boost = (c: PublicChef) =>
        (c.is_sponsored ? 32 : 0) +
        (c.premium_tier === "gold" ? 8 : 0) +
        (c.premium_tier === "silver" ? 4 : 0) +
        (c.verified_badge ? 1 : 0);
      const aBoost = boost(a);
      const bBoost = boost(b);
      if (aBoost !== bBoost) return bBoost - aBoost;
      if (a.rating_avg !== b.rating_avg) return b.rating_avg - a.rating_avg;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    return chefs;
  });

const bySlugInput = z.object({ slug: z.string().regex(/^[a-z0-9-]{1,80}$/i) });

export const getPublicChefBySlug = createServerFn({ method: "GET" })
  .validator((d: { slug: string }) => bySlugInput.parse(d))
  .handler(async ({ data }) => {
    const { getPublicSupabase } = await import("./supabase.public.server");
    const sb = getPublicSupabase();
    // Sprint 23.6 — anche il profilo singolo è accessibile solo con almeno
    // un annuncio attivo.
    const { data: row, error } = await sb
      .from("chefs")
      .select(`${PROJECTION}, chef_listings!inner(id)`)
      .eq("chef_listings.is_active", true)
      .eq("slug", data.slug)
      .eq("is_visible", true)
      .eq("verified_badge", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const chef = mapRow(row as unknown as Row);
    const { data: media } = await sb
      .from("chef_media")
      .select("id, kind, url, is_cover, position")
      .eq("chef_id", chef.user_id)
      .order("position", { ascending: true });
    // Sprint 19.1 — Visibilità foto per tier (free 3 / silver 5 / gold 10).
    // Le foto NON vengono mai eliminate: vengono solo nascoste oltre il limite.
    // Cover/avatar/menu/video non sono soggetti al limite foto.
    const all = (media ?? []) as PublicChefMedia[];
    const limit = PHOTO_LIMITS[chef.premium_tier];
    let shown = 0;
    const filtered = all.filter((m) => {
      if (m.kind !== "photo") return true;
      if (shown < limit) { shown += 1; return true; }
      return false;
    });

    // Sprint 19.1 — WhatsApp pubblico solo per Silver/Gold con consenso esplicito.
    let whatsapp: string | null = null;
    if (chef.premium_tier !== "free") {
      const { data: contact } = await sb
        .from("chef_contacts")
        .select("whatsapp, whatsapp_public")
        .eq("chef_id", chef.user_id)
        .maybeSingle();
      if (contact?.whatsapp_public && contact.whatsapp) {
        whatsapp = contact.whatsapp as string;
      }
    }

    return { chef, media: filtered, whatsapp };
  });

export const listPublicSlugs = createServerFn({ method: "GET" }).handler(async () => {
  const { getPublicSupabase } = await import("./supabase.public.server");
  const sb = getPublicSupabase();
  const { data, error } = await sb
    .from("chefs")
    .select("slug, updated_at, chef_listings!inner(id)")
    .eq("chef_listings.is_active", true)
    .eq("is_visible", true)
    .eq("verified_badge", true)
    .limit(5000);
  if (error) throw new Error(error.message);
  // Dedup per slug (un chef con più annunci attivi può comparire più volte
  // a seconda dell'embed PostgREST).
  const seen = new Set<string>();
  const out: Array<{ slug: string; updated_at: string }> = [];
  for (const r of (data ?? []) as Array<{ slug: string; updated_at: string }>) {
    if (!seen.has(r.slug)) { seen.add(r.slug); out.push({ slug: r.slug, updated_at: r.updated_at }); }
  }
  return out;
});

export const chefQueries = {
  list: (limit?: number) =>
    queryOptions({
      queryKey: ["public-chefs", "list", limit ?? null] as const,
      queryFn: () => listPublicChefs({ data: { limit } }),
      staleTime: 60_000,
    }),
  bySlug: (slug: string) =>
    queryOptions({
      queryKey: ["public-chefs", "slug", slug] as const,
      queryFn: () => getPublicChefBySlug({ data: { slug } }),
      staleTime: 60_000,
    }),
};